import React, { useState, useMemo } from 'react';
import { useData } from '../../context/DataContext';
import { User } from '../../types';
import { 
  TrashIcon, 
  XIcon, 
  ClockIcon,
  ShieldCheckIcon,
  DeliveryIcon
} from '../../components/icons';

const SuperAdminDrivers: React.FC = () => {
  const { 
    allLivreurs, 
    validateLivreur, 
    suspendLivreur, 
    deleteLivreur
  } = useData();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'suspended'>('all');

  const filteredDrivers = useMemo(() => {
    return allLivreurs.filter(l => {
      const matchesSearch = (l.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                            (l.wilaya || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (l.phone || '').includes(searchTerm);
      const matchesFilter = filter === 'all' || l.livreurStatus === filter;
      return matchesSearch && matchesFilter;
    });
  }, [allLivreurs, searchTerm, filter]);

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <h1 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
        <DeliveryIcon className="w-8 h-8 text-blue-600" />
        Gestion des Livreurs
      </h1>
      
      {/* Filters & Search */}
      <div className="flex flex-col gap-4 mb-6">
        <input 
          type="text" 
          placeholder="Rechercher un livreur (nom, wilaya, tel)..." 
          className="w-full p-4 rounded-2xl bg-white border border-gray-100 shadow-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {(['all', 'pending', 'approved', 'suspended'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest whitespace-nowrap transition-all ${
                filter === f ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-white text-gray-400 border border-gray-100'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : f === 'approved' ? 'Actifs' : 'Suspendus'}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-4">
        {filteredDrivers.length === 0 ? (
          <div className="bg-white p-12 rounded-3xl text-center border-2 border-dashed border-gray-100">
             <p className="text-gray-400 font-bold">Aucun livreur trouvé.</p>
          </div>
        ) : (
          filteredDrivers.map((livreur) => (
            <div 
              key={livreur.uid} 
              className="bg-white p-5 rounded-3xl shadow-sm border border-gray-50 flex flex-col md:flex-row gap-4 items-center"
            >
              <div className="flex items-center gap-4 flex-1 w-full">
                 <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600">
                    <DeliveryIcon className="w-8 h-8" />
                 </div>
                 <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-black text-gray-900">{livreur.name}</h3>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase ${
                        livreur.livreurStatus === 'approved' ? 'bg-green-100 text-green-700' : 
                        livreur.livreurStatus === 'pending' ? 'bg-orange-100 text-orange-700' : 
                        'bg-red-100 text-red-700'
                      }`}>
                        {livreur.livreurStatus}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 font-bold tracking-tight">{livreur.wilaya || 'N/A'} • {livreur.phone || 'Pas de tel'}</p>
                    <p className="text-[10px] text-gray-400 font-medium">{livreur.email}</p>
                    
                    {/* Statistiques persistantes */}
                    <div className="flex gap-4 mt-2">
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] grayscale">⭐</span>
                            <span className="text-[10px] font-bold text-amber-600">{(livreur.rating || 0).toFixed(1)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] grayscale">🍴</span>
                            <span className="text-[10px] font-bold text-blue-600">{(livreur.restaurantRating || 0).toFixed(1)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] grayscale">📦</span>
                            <span className="text-[10px] font-bold text-gray-600">{livreur.deliveredOrdersCount || 0}</span>
                        </div>
                    </div>
                 </div>
              </div>

              <div className="flex gap-2 w-full md:w-auto">
                 {livreur.livreurStatus === 'pending' && (
                   <button 
                    onClick={() => validateLivreur(livreur.uid, 'approved')}
                    className="flex-1 md:flex-none bg-green-600 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-green-700 transition-colors shadow-lg shadow-green-100"
                   >
                     Valider
                   </button>
                 )}
                 <button 
                  onClick={() => { if(confirm('Suspendre?')) suspendLivreur(livreur.uid) }}
                  className="bg-gray-100 text-gray-600 p-2 rounded-xl hover:bg-gray-200 transition-colors"
                  title="Suspendre"
                 >
                   <ClockIcon className="w-5 h-5" />
                 </button>
                 <button 
                  onClick={() => { if(confirm('Supprimer?')) deleteLivreur(livreur.uid) }}
                  className="bg-red-50 text-red-600 p-2 rounded-xl hover:bg-red-100 transition-colors"
                  title="Supprimer"
                 >
                   <TrashIcon className="w-5 h-5" />
                 </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SuperAdminDrivers;
